import React, { useState } from 'react';
import axios from 'axios';
import './ConfigDownload.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function ConfigDownload({ docType, docVersion, showToast }) {
  const [generating, setGenerating] = useState(false);
  const [downloadingSummaries, setDownloadingSummaries] = useState(false);

  const handleGenerateAndDownload = async () => {
    if (!docType) {
      showToast('Select a document first', 'error');
      return;
    }
    setGenerating(true);
    showToast('Running config generation (Mongo, descriptions, etc.)…', 'success', 5000);
    try {
      const params = new URLSearchParams();
      if (docVersion) params.append('version', docVersion);
      const url = `${API_BASE}/config/generate/${docType}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.post(url, null, {
        responseType: 'blob',
        timeout: 300000, // 5 min – pipeline runs Mongo pull, descriptions, etc.
      });
      const disposition = response.headers['content-disposition'];
      let filename = `${docType}_configs.zip`;
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1].trim();
      }
      const blob = new Blob([response.data], { type: 'application/zip' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      showToast('Configs generated and downloaded (main config + validation config with rules)', 'success');
    } catch (error) {
      console.error('Generate/download failed:', error);
      let message = 'Generate failed';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed.detail || message;
        } catch (_) {}
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      showToast(message, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadSummaries = async () => {
    if (!docType) {
      showToast('Select a document first', 'error');
      return;
    }
    setDownloadingSummaries(true);
    try {
      const params = new URLSearchParams();
      params.append('download', 'true');
      if (docVersion) params.append('version', docVersion);
      const url = `${API_BASE}/config/summaries/${docType}?${params.toString()}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'];
      let filename = `${docType}_rule_summaries.txt`;
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1].trim();
      }
      const blob = new Blob([response.data], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      showToast('Rule summaries downloaded', 'success');
    } catch (error) {
      console.error('Download summaries failed:', error);
      let message = 'Download failed';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed.detail || message;
        } catch (_) {}
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      showToast(message, 'error');
    } finally {
      setDownloadingSummaries(false);
    }
  };

  if (!docType) return null;

  return (
    <div className="config-download">
      <button
        type="button"
        className="config-download-btn"
        onClick={handleGenerateAndDownload}
        disabled={generating}
        title="Run Main_Config_Gen for this document (Mongo pull, descriptions, main config + validation config) and download both files as ZIP. Validation config includes rules from this app."
      >
        {generating ? 'Generating…' : 'Generate & download configs'}
      </button>
      <button
        type="button"
        className="config-download-btn config-summaries-btn"
        onClick={handleDownloadSummaries}
        disabled={downloadingSummaries}
        title="Download natural language summaries of all validation rules (required fields, conditional rules, unconditional rules, custom requirements)"
      >
        {downloadingSummaries ? 'Downloading…' : 'Download rule summaries'}
      </button>
    </div>
  );
}

export default ConfigDownload;
